import { useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Stack, Typography, IconButton } from '@mui/material';
import QRCodeIcon from '@mui/icons-material/QrCode2Rounded';
import { useDispatch, useSelector } from 'react-redux';

import { downloadQR, ensureBasketQrCode } from 'redux/slices/qr/qrCodeSlice';
import { GenerateQRProps } from 'types/common';
import CustomButton from 'components/Common/Button';
import InfoModal from 'components/Common/InfoModal';
import MuiLoader from 'components/Common/Loader';
import Colors from 'constants/colors';
import { AppDispatch, RootState } from 'redux/store';
import { selectSelectedParishId } from 'redux/selectors/parishContext';

/**
 * Basket-detail QR entry: shows the server-signed payloadUrl of *this basket's* code.
 * No client deep links (QR_BASE_URL / APP_PROTOCOL removed).
 */
const GenerateQRCode: React.FC<GenerateQRProps> = ({ basket }) => {
    const dispatch = useDispatch<AppDispatch>();
    const parishId = useSelector(selectSelectedParishId);
    const { activeQr, loading, inflightParishId, inflightBasketId } = useSelector((state: RootState) => state.qrCode);
    const [showQR, setShowQR] = useState(false);

    // `Basket.id` is typed `number | string`, but normalizeBasket casts the backend's uuid
    // string through `as unknown as number` — the runtime value is the uuid the API wants.
    const basketId = basket?.id ? String(basket.id) : null;

    useEffect(() => {
        if (!showQR || !parishId || !basketId) return;
        dispatch(
            ensureBasketQrCode({
                parishId,
                basketId,
                label: basket?.name || 'Parish QR',
                description: 'default',
            })
        );
    }, [showQR, parishId, basketId, basket?.name, dispatch]);

    // Never paint a QR whose ensure/load was for a different parish OR a different basket —
    // printing another basket's code routes real donations to the wrong fund (H2).
    const displayQr =
        parishId &&
        basketId &&
        inflightParishId === parishId &&
        inflightBasketId === basketId &&
        activeQr?.basketId === basketId &&
        activeQr?.payloadUrl
            ? activeQr
            : null;

    const handleDownloadQR = () => {
        if (!displayQr?.payloadUrl) return;
        downloadQR('generate-qr-code', displayQr.id || String(basket?.id || 'qrCode'));
    };

    const title = basket?.name || 'Parish QR';

    return (
        <>
            <IconButton onClick={() => setShowQR(true)}>
                <QRCodeIcon htmlColor={Colors.darkblue} sx={{ width: 50, height: 50 }} />
            </IconButton>
            <InfoModal title="" isOpen={showQR} onClose={() => setShowQR(false)} hideActionButtons>
                <>
                    <MuiLoader absolute loading={loading} />
                    <Stack alignItems="center" mb={5}>
                        <Typography className="two-line-text" fontSize="35px" mb={3}>
                            {title}
                        </Typography>
                        {displayQr?.payloadUrl ? (
                            <QRCodeCanvas id="generate-qr-code" value={displayQr.payloadUrl} size={300} />
                        ) : (
                            !loading && (
                                <Typography variant="body1" color="text.secondary">
                                    No QR code available for this basket.
                                </Typography>
                            )
                        )}
                    </Stack>
                    <Stack width="100%" alignItems="center">
                        <CustomButton width={200} onClick={handleDownloadQR} disabled={!displayQr?.payloadUrl}>
                            Download
                        </CustomButton>
                    </Stack>
                </>
            </InfoModal>
        </>
    );
};

export default GenerateQRCode;
